const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/labReports/generalHandlers.ts';
import Imm from 'immutable';
import moment from 'moment';
import { Logger } from '../../services/KisLoggerService';
import { KISIM_TIME_FORMAT } from '../../defs/generalDefs';
import { decCounter, incCounter } from '../../utils/reducerUtils';
import {
    AbortLoadingCumulSummaryAction,
    AboutToLoadCumulSummaryAction,
    CompletedLoadingCumulSummaryAction,
    ExtendCumulAvailLimitAction,
    Forget1CumulReportAction,
    SetDesiredNumColumnsAction,
    SetFetchOptionsAction,
    Truncate1CumulReportAction,
} from '../../actions/labReportsActions';
import * as LabDefs from '../../defs/labReportDefs';

const Log = Logger('labReportsReducer/general');
const emptyMap = Imm.Map<string, any>();

function getDefaultAvailLimit(state: Imm.Map<string, any>, numDaysIntoPast: number = 0) {
    const amount = Number(state.getIn(['initFetchInterval', 'amount']) || 0);
    const units = state.getIn(['initFetchInterval', 'units']) || 'seconds';
    return moment().subtract(amount, units).subtract(numDaysIntoPast, 'days').format(KISIM_TIME_FORMAT);
}

export function aboutToLoadCumulSummaryHandler(state: Imm.Map<string, any>, action: AboutToLoadCumulSummaryAction) {
    const repId = `${action.patnr}_${action.defsId}`;
    let report = state.getIn(['cumulReports', repId]);

    if (!report) {
        // Initialize the new report with the defaults.
        report = emptyMap
            .set('availLimit', getDefaultAvailLimit(state))
            .set('desiredNumColumns', LabDefs.MAX_NUM_COLUMNS_VISIBLE);
    }

    // Start the chain of actions that search for lab results.
    const availMinDat = report.get('availMinDat'); // will be undefined as long as availList is still empty
    report = report.set('chain', true).set('chainStartDat', availMinDat);

    state = state.setIn(['cumulReports', repId], report);
    return incCounter(state, ['cumulReports', repId, 'loading']);
}

export function completedLoadingCumulSummaryHandler(
    state: Imm.Map<string, any>,
    action: CompletedLoadingCumulSummaryAction
) {
    const repId = `${action.patnr}_${action.defsId}`;

    // Delete the chain flag to indicate the loading chain has stopped.
    // However, do not delete chainStartDat, because trackVisibleColumnSaga still needs it.
    state = state.deleteIn(['cumulReports', repId, 'chain']);

    if (state.getIn(['cumulReports', repId, 'desiredNumColumns']) === 'unlimited') {
        const availList = state.getIn(['cumulReports', repId, 'availList']);
        const numItems = !availList ? 0 : availList.size;
        const desiredNumColumns = Math.max(numItems, LabDefs.MAX_NUM_COLUMNS_VISIBLE);
        Log.info(
            [
                `availList now contains ${numItems} items.`,
                'desiredNumColumns was unlimited.',
                `Setting it back to ${desiredNumColumns}`,
            ].join(' ')
        );
        state = state.setIn(['cumulReports', repId, 'desiredNumColumns'], desiredNumColumns);
    }

    return decCounter(state, ['cumulReports', repId, 'loading']);
}

export function abortLoadingCumulSummaryHandler(state: Imm.Map<string, any>, action: AbortLoadingCumulSummaryAction) {
    const repId = `${action.patnr}_${action.defsId}`;
    state = state.deleteIn(['cumulReports', repId, 'chain']);
    return decCounter(state, ['cumulReports', repId, 'loading']);
}

export function extendCumulAvailLimitHandler(state: Imm.Map<string, any>, action: ExtendCumulAvailLimitAction) {
    const repId = `${action.patnr}_${action.defsId}`;
    const report = state.getIn(['cumulReports', repId]);

    if (!report) {
        // Oops, this shouldn't happen!
        Log.warn(`Refusing to extend the avail limit of a missing report.`);
        return state;
    }

    let availLimit = report.get('availLimit');
    const hasAvailLimit = !!availLimit;

    if (!hasAvailLimit) {
        // Start with a default limit.
        availLimit = getDefaultAvailLimit(state, LabDefs.EXTEND_AVAIL_LIMIT_NUM_DAYS);
    }

    if (action.availLimit) {
        // The action specifies the desired new limit. However, we ignore it if the
        // current limit is already furhter in the past.
        if (action.availLimit < availLimit) {
            availLimit = action.availLimit;
        }
    } else if (hasAvailLimit) {
        // The action did not specify the new limit. Extend the current limit some
        // reasonable amount.
        availLimit = moment(availLimit, KISIM_TIME_FORMAT)
            .subtract(LabDefs.EXTEND_AVAIL_LIMIT_NUM_DAYS, 'days')
            .format(KISIM_TIME_FORMAT);
    }

    Log.info(`Setting availLimit to: ${availLimit}`);
    return state.setIn(['cumulReports', repId, 'availLimit'], availLimit);
}

export function setDesiredNumColumnsHandler(state: Imm.Map<string, any>, action: SetDesiredNumColumnsAction) {
    const repId = `${action.patnr}_${action.defsId}`;

    // The following checks were made to track down some bug. Keeping it in won't hurt.

    if (typeof action.desiredNumColumns === 'number') {
        if (Number.isNaN(action.desiredNumColumns)) {
            Log.warn(`Refusing to set desiredNumColumns to NaN`);
            return state;
        } else if (action.desiredNumColumns < LabDefs.MAX_NUM_COLUMNS_VISIBLE) {
            Log.warn(`Refusing to set desiredNumColumns to a value less than ${LabDefs.MAX_NUM_COLUMNS_VISIBLE}`);
            return state;
        }
    } else if (action.desiredNumColumns !== 'unlimited') {
        Log.warn(`Refusing to set desiredNumColumns to ${action.desiredNumColumns}`);
        return state;
    }

    return state.setIn(['cumulReports', repId, 'desiredNumColumns'], action.desiredNumColumns);
}

export function setFetchOptionsHandler(state: Imm.Map<string, any>, action: SetFetchOptionsAction) {
    return state
        .setIn(['initFetchInterval', 'amount'], action.initFetchIntervalAmount || 0)
        .setIn(['initFetchInterval', 'units'], action.initFetchIntervalUnits || 'seconds');
}

export function truncate1CumulReportHandler(state: Imm.Map<string, any>, action: Truncate1CumulReportAction) {
    const repId = `${action.patnr}_${action.defsId}`;
    let report = state.getIn(['cumulReports', repId]);

    if (report) {
        if ((report.get('loading') || 0) > 0) {
            Log.warn('Refusing to truncate a report for which a request is still pending!');
        } else {
            let availList = report.get('availList');

            if (availList && availList.size > action.maxNumColumns) {
                // Drop the excessive columns.

                availList = availList.take(action.maxNumColumns);
                report = report.set('availList', availList);

                // Set availMinDat to the oldest entry remaining.

                const newAvailMinDat = availList.reverse().getIn([0, 'Date']);
                report = report.set('availMinDat', newAvailMinDat);
                // Log.info (`The new availMinDat is: ${newAvailMinDat}`);

                // Set availLimit to the minimum of availMinDat and the default availLimit.

                const defaultLimit = getDefaultAvailLimit(state);

                if (newAvailMinDat < defaultLimit) {
                    // Some of the remaining avail items are older than the default limit.
                    report = report.set('availLimit', newAvailMinDat);
                } else {
                    // The default limit is good enough.
                    report = report.set('availLimit', defaultLimit);
                }

                // Iterate over the remaining columns and note which cells and rows are to be kept.

                const rowDefs = report.get('rowDefs');

                if (rowDefs) {
                    const cellsToBeKept: string[] = [];
                    const rowsToBeKept: string[] = [];

                    availList.forEach((availItem: Imm.Map<string, any>) => {
                        const colId = availItem.get('colId');

                        if (colId) {
                            rowDefs.forEach((_row: Imm.Map<string, any>, rowId: string) => {
                                const cellId = `${colId}_${rowId}`;

                                if (report.getIn(['data', cellId])) {
                                    cellsToBeKept.push(cellId);
                                    rowsToBeKept.push(rowId);
                                }
                            });
                        }
                    });

                    // Discard cells which are no longer needed.

                    let data = emptyMap;

                    cellsToBeKept.forEach((cellId: string) => {
                        data = data.set(cellId, report.getIn(['data', cellId]));
                    });

                    // Log.info (`Previous num cells: ${(report.get ('data') || { size: 0 }).size}`);
                    report = report.set('data', data);
                    // Log.info (`New num cells: ${(report.get ('data') || { size: 0 }).size}`);

                    // Discard rows which are no longer needed.

                    let newRowDefs = Imm.OrderedMap<string, any>();

                    rowsToBeKept.forEach((rowId: string) => {
                        newRowDefs = newRowDefs.set(rowId, rowDefs.get(rowId));
                    });

                    // Keep rows sorted by Pos.

                    newRowDefs = newRowDefs.sort((row1: Imm.Map<string, any>, row2: Imm.Map<string, any>) => {
                        const p1 = row1.get('Pos') || 'ZZZZ';
                        const p2 = row2.get('Pos') || 'ZZZZ';
                        return p1 < p2 ? -1 : p1 > p2 ? 1 : 0;
                    }) as any;

                    // Log.info (`Previous num rows: ${(report.get ('rowDefs') || { size: 0 }).size}`);
                    report = report.set('rowDefs', newRowDefs);
                    // Log.info (`New num rows: ${(report.get ('rowDefs') || { size: 0 }).size}`);
                }

                // Reset desiredNumColumns and put the report back into the app state.

                report = report.set('desiredNumColumns', LabDefs.MAX_NUM_COLUMNS_VISIBLE);
                state = state.setIn(['cumulReports', repId], report);
            }
        }
    }

    return state;
}

export function forget1CumulReportHandler(state: Imm.Map<string, any>, action: Forget1CumulReportAction) {
    const repId = `${action.patnr}_${action.defsId}`;
    const report = state.getIn(['cumulReports', repId]);

    if (report) {
        if ((report.get('loading') || 0) > 0) {
            Log.warn('Refusing to drop a report for which a request is still pending!');
        } else {
            // Throw away the entire report.
            state = state.deleteIn(['cumulReports', repId]);
        }
    }

    return state;
}
